/**
 * Schema utilities and display constants.
 *
 * Extracted from mocks/mappingData.ts so they can be used with
 * both mock data and real API responses.
 */

import type { SchemaField, MappingStatus } from "@/types";

// ── Static display config (no data dependency) ─────────────

export const STATUS_CONFIG: Record<
  MappingStatus,
  { label: string; bg: string; text: string; border: string }
> = {
  known_mapping: {
    label: "Known Mapping",
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    border: "border-emerald-200",
  },
  ML: {
    label: "ML",
    bg: "bg-amber-50",
    text: "text-amber-700",
    border: "border-amber-200",
  },
  LLM: {
    label: "LLM",
    bg: "bg-purple-50",
    text: "text-purple-700",
    border: "border-purple-200",
  },
  needs_match: {
    label: "Needs Match",
    bg: "bg-red-50",
    text: "text-red-700",
    border: "border-red-200",
  },
  mapped_by_user: {
    label: "Mapped by User",
    bg: "bg-sky-50",
    text: "text-sky-700",
    border: "border-sky-200",
  },
};

export const STATUS_SORT_ORDER: Record<MappingStatus, number> = {
  needs_match: 0,
  LLM: 1,
  ML: 2,
  mapped_by_user: 3,
  known_mapping: 4,
};

// ── Dynamic lookups built from SchemaField[] ────────────────

export interface SchemaLookups {
  fields: SchemaField[];
  byId: Map<string, SchemaField>;
  byName: Map<string, SchemaField>;
  mandatoryFields: SchemaField[];
  additionalFields: SchemaField[];
  mandatoryFieldIds: Set<string>;
  getFieldName: (id: string) => string;
}

export function buildSchemaLookups(fields: SchemaField[]): SchemaLookups {
  const byId = new Map(fields.map((f) => [f.id, f]));
  const byName = new Map(fields.map((f) => [f.fieldName, f]));
  const mandatoryFields = fields.filter((f) => f.mandatory);
  const additionalFields = fields.filter((f) => !f.mandatory);
  const mandatoryFieldIds = new Set(mandatoryFields.map((f) => f.id));

  return {
    fields,
    byId,
    byName,
    mandatoryFields,
    additionalFields,
    mandatoryFieldIds,
    getFieldName: (id: string) => byId.get(id)?.fieldName ?? "",
  };
}
